import axios from "axios";
import moment from "moment";
import { message } from "antd";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import Compress from "./Compress";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Descarga masiva del Reporte de Conciliacion Facturas - Pagos: un ZIP con una
 * CARPETA POR FACTURA, y dentro el XML y el PDF de la factura más los de cada uno
 * de sus complementos de pago.
 *
 * No usa el registro `MODULOS` de `DescargarDocumentosMasivo` porque ahí un
 * renglón es un documento y aquí un renglón es una factura MÁS sus N
 * complementos; sí comparte todas sus defensas (tandas, logos deduplicados,
 * ceder el hilo, nombres únicos, `Compress`).
 *
 * Igual que el resto de las descargas del proyecto, el servidor NO renderiza
 * ningún PDF: manda los datos y el navegador los arma con pdfmake.
 */

// Ids por petición. Los dos endpoints tienen su propio tope de 100; esto mantiene
// cada respuesta chica y da avance visible.
const TAMANO_TANDA = 20;

// pdfmake 0.1.x renderiza en el hilo principal: sin ceder el hilo cada tantos
// documentos la pestaña se congela hasta terminar el lote.
const DOCUMENTOS_ENTRE_PAUSAS = 5;

const enTandas = (arreglo, tamano) => {
  const tandas = [];
  for (let i = 0; i < arreglo.length; i += tamano) tandas.push(arreglo.slice(i, i + tamano));
  return tandas;
};

const cederElHilo = () => new Promise((resolve) => setTimeout(resolve, 0));

// JSZip trata la diagonal como separador de carpetas, así que se limpia CADA
// segmento por separado: la ruta se arma después, a propósito.
const limpiarSegmento = (texto) => (texto || "").replace(/[/\\?*[\]:#]/g, "_").trim();

/** `JSZip.file()` sobreescribe en silencio si la ruta se repite. */
const rutaUnica = (ruta, usadas) => {
  if (!usadas.has(ruta)) {
    usadas.add(ruta);
    return ruta;
  }
  const punto = ruta.lastIndexOf(".");
  const base = punto > 0 ? ruta.slice(0, punto) : ruta;
  const extension = punto > 0 ? ruta.slice(punto) : "";
  let intento = 2;
  while (usadas.has(`${base} (${intento})${extension}`)) intento += 1;
  const unica = `${base} (${intento})${extension}`;
  usadas.add(unica);
  return unica;
};

/**
 * El backend manda el base64 de cada logo una sola vez por lote; aquí se vuelve a
 * pegar en cada documento antes de generar su PDF.
 */
const rehidratarLogos = (values, logos) => {
  const pegar = (contenedor, campo) => {
    const logo = contenedor ? contenedor[campo] : null;
    if (!logo || !logo.claveLogo) return;
    const guardado = logos ? logos[logo.claveLogo] : null;
    if (!guardado) return;
    contenedor[campo] = { ...logo, base64: guardado.base64, base64Completa: guardado.base64Completa };
  };
  pegar(values && values.respuestaFactura && values.respuestaFactura.empresa, "logo");
  pegar(values && values.respuestaComplemento && values.respuestaComplemento.empresa, "logo");
  pegar(values, "logoBiwo");
};

const generarPdfBlob = (docDefinition) =>
  new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBlob((blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });

/** Pide los `values` de una lista de ids, en tandas, y los indexa por id. */
const pedirEnTandas = async ({ endpoint, ids, cuerpo, headersToken, onDocumento }) => {
  const porId = new Map();
  let fallidos = 0;

  for (const tanda of enTandas(ids, TAMANO_TANDA)) {
    const res = await axios.post(endpoint, { ...cuerpo, ids: tanda }, { headers: headersToken });
    if (res.data.success !== 2) {
      throw new Error(res.data.message || "Sucedió un error al preparar la descarga");
    }
    for (const documento of res.data.documentos) {
      if (!documento.ok) {
        fallidos += 1;
        if (onDocumento) onDocumento();
        continue;
      }
      rehidratarLogos(documento.values, res.data.logos);
      porId.set(documento.id.toString(), documento.values);
      if (onDocumento) onDocumento();
    }
  }

  return { porId, fallidos };
};

/** `Serie - Folio ( RFC - Nombre )`, la misma fórmula de la descarga individual. */
const carpetaDeFactura = (values, registro) => {
  const dataSat = (values && values.respuestaFactura && values.respuestaFactura.dataSat) || {};
  const receptor = dataSat.Receptor || {};
  if (dataSat.Folio) {
    return limpiarSegmento(
      `${dataSat.Serie || "F"} - ${dataSat.Folio} ( ${receptor.Rfc || ""} - ${receptor.Nombre || ""} )`
    );
  }
  // Sin `values` (la factura no se pudo traer) se cae al renglón del reporte.
  return limpiarSegmento(`${registro.folio} ( ${registro.rfc || ""} - ${registro.cliente || ""} )`);
};

/**
 * @param {Object} params
 * @param {Object[]} params.registros   renglones seleccionados del reporte
 * @param {string} params.empresaId
 * @param {Object} params.headersToken
 * @param {boolean} params.incluirPdfFactura
 * @param {boolean} params.incluirXmlFactura
 * @param {boolean} params.incluirPdfComplemento
 * @param {boolean} params.incluirXmlComplemento
 * @param {boolean} params.incluirExcel        el Excel de la selección, en la raíz del ZIP
 * @param {Function} [params.construirExcel]   `(registros) => Blob`
 * @param {Function} [params.onProgreso]       `(hechos, total) => void`
 * @returns {Promise<boolean>} true si se descargó algo
 */
const descargarConciliacionMasivo = async ({
  registros, empresaId, headersToken,
  incluirPdfFactura = true, incluirXmlFactura = true,
  incluirPdfComplemento = true, incluirXmlComplemento = true,
  incluirExcel = false, construirExcel, onProgreso,
}) => {
  if (!registros || registros.length === 0) {
    message.info("No hay facturas seleccionadas");
    return false;
  }
  const quiereFactura = incluirPdfFactura || incluirXmlFactura;
  const quiereComplemento = incluirPdfComplemento || incluirXmlComplemento;
  if (!quiereFactura && !quiereComplemento && !incluirExcel) {
    message.info("Elige al menos un formato para descargar");
    return false;
  }

  const ventaIds = registros.map((registro) => registro.ventaId.toString());
  // Un mismo complemento puede amparar varias de las facturas seleccionadas: se
  // pide UNA vez y su archivo se copia a la carpeta de cada factura.
  const pagoIds = [...new Set(
    registros.flatMap((registro) =>
      (registro.complementos || []).map((complemento) => complemento.pagoId.toString())
    )
  )];

  const totalPasos = (quiereFactura ? ventaIds.length : 0)
    + (quiereComplemento ? pagoIds.length : 0);
  let hechos = 0;
  const avanzar = () => {
    hechos += 1;
    if (onProgreso) onProgreso(hechos, totalPasos);
  };

  const archivos = [];
  const rutasUsadas = new Set();
  let fallidos = 0;

  try {
    // Los generadores pesan varios cientos de KB: se cargan bajo demanda y sólo
    // los que hagan falta.
    const [facturaPdf, complementoPdf] = await Promise.all([
      incluirPdfFactura ? import("../Pdf/facturaPdf").then((modulo) => modulo.default) : null,
      incluirPdfComplemento ? import("../Pdf/complementoPdf").then((modulo) => modulo.default) : null,
    ]);

    const necesitaLogosFactura = incluirPdfFactura;
    const necesitaLogosComplemento = incluirPdfComplemento;

    const facturas = quiereFactura
      ? await pedirEnTandas({
        endpoint: "ventas/datosPdfMasivo",
        ids: ventaIds,
        cuerpo: { empresaId, incluirLogos: necesitaLogosFactura },
        headersToken,
        onDocumento: avanzar,
      })
      : { porId: new Map(), fallidos: 0 };

    const complementos = quiereComplemento && pagoIds.length > 0
      ? await pedirEnTandas({
        endpoint: "pagos/datosComplementoMasivo",
        ids: pagoIds,
        cuerpo: { empresaId, incluirLogos: necesitaLogosComplemento },
        headersToken,
        onDocumento: avanzar,
      })
      : { porId: new Map(), fallidos: 0 };

    fallidos = facturas.fallidos + complementos.fallidos;

    // Los PDF se generan una sola vez por documento aunque el archivo se copie a
    // varias carpetas.
    const pdfFacturaPorVenta = new Map();
    const pdfComplementoPorPago = new Map();
    let generados = 0;

    for (const registro of registros) {
      const ventaId = registro.ventaId.toString();
      const values = facturas.porId.get(ventaId);
      const carpeta = carpetaDeFactura(values, registro);

      if (values) {
        if (incluirXmlFactura) {
          const xml = values.respuestaFactura && values.respuestaFactura.data
            ? values.respuestaFactura.data.cfdi
            : null;
          if (xml) {
            archivos.push({
              nombre: rutaUnica(`${carpeta}/Factura ${carpeta}.xml`, rutasUsadas),
              contenido: xml,
            });
          }
        }

        if (incluirPdfFactura && facturaPdf) {
          try {
            if (!pdfFacturaPorVenta.has(ventaId)) {
              pdfFacturaPorVenta.set(ventaId, await generarPdfBlob(facturaPdf(values)));
              generados += 1;
              if (generados % DOCUMENTOS_ENTRE_PAUSAS === 0) await cederElHilo();
            }
            archivos.push({
              nombre: rutaUnica(`${carpeta}/Factura ${carpeta}.pdf`, rutasUsadas),
              contenido: pdfFacturaPorVenta.get(ventaId),
            });
          } catch (error) {
            console.error("Error al generar el PDF de la factura", ventaId, error);
            fallidos += 1;
          }
        }
      }

      for (const complemento of registro.complementos || []) {
        const pagoId = complemento.pagoId.toString();
        const valuesComplemento = complementos.porId.get(pagoId);
        if (!valuesComplemento) continue;
        const nombreComplemento = limpiarSegmento(
          `Complemento ${complemento.folio || complemento.uuid || pagoId}`
        );

        if (incluirXmlComplemento) {
          const xml = valuesComplemento.respuestaComplemento
            && valuesComplemento.respuestaComplemento.data
            ? valuesComplemento.respuestaComplemento.data.cfdi
            : null;
          if (xml) {
            archivos.push({
              nombre: rutaUnica(`${carpeta}/${nombreComplemento}.xml`, rutasUsadas),
              contenido: xml,
            });
          }
        }

        if (incluirPdfComplemento && complementoPdf) {
          try {
            if (!pdfComplementoPorPago.has(pagoId)) {
              pdfComplementoPorPago.set(pagoId, await generarPdfBlob(complementoPdf(valuesComplemento)));
              generados += 1;
              if (generados % DOCUMENTOS_ENTRE_PAUSAS === 0) await cederElHilo();
            }
            archivos.push({
              nombre: rutaUnica(`${carpeta}/${nombreComplemento}.pdf`, rutasUsadas),
              contenido: pdfComplementoPorPago.get(pagoId),
            });
          } catch (error) {
            console.error("Error al generar el PDF del complemento", pagoId, error);
            fallidos += 1;
          }
        }
      }
    }

    if (incluirExcel && construirExcel) {
      try {
        archivos.push({
          nombre: "Conciliacion_Facturas_Pagos.xlsx",
          contenido: construirExcel(registros),
        });
      } catch (error) {
        console.error("Error al generar el Excel de la seleccion", error);
        fallidos += 1;
      }
    }

    if (archivos.length === 0) {
      message.warning("No se pudo generar ningún archivo con lo seleccionado");
      return false;
    }

    Compress(archivos, `Conciliacion_Facturas_Pagos_${moment().format("YYYY-MM-DD")}`);

    if (fallidos > 0) {
      message.warning(
        fallidos === 1
          ? "1 documento no se pudo generar y quedó fuera del ZIP"
          : `${fallidos} documentos no se pudieron generar y quedaron fuera del ZIP`
      );
    }
    return true;
  } catch (error) {
    console.error("Error en la descarga masiva de la conciliacion:", error);
    message.error(error.message || "Sucedió un error al descargar los documentos");
    return false;
  }
};

export default descargarConciliacionMasivo;
